/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * What a profile field is, before anything draws it.
 *
 * A field is either owned by a system of record, in which case it echoes that value and cannot be
 * typed in, or it is the user's to fill in. Which of the two is decided by the server and read here
 * through {@link resolve}, so no dialog re-derives the rule.
 */

import type { UserProfile } from '@/api/settings';
import type { GeoOption } from '@/lib/geoOptions';

/**
 * Where a locked value came from, so a dialog can say why the field cannot be typed in.
 *
 * `account` and `gdl` are the server's own answer. `profile` is added by a dialog that only borrows
 * a detail the user has already settled elsewhere, and so shows it rather than inviting an edit.
 */
export type FieldSource = 'account' | 'gdl' | 'profile';

/** A field the user may or may not own: locked ones echo the system of record. */
export interface ResolvedField {
  value: string;
  locked: boolean;
  source: FieldSource | null;
}

/** The editable slice of a profile, and the keys the server reports provenance for. */
export type FieldKey = keyof NonNullable<UserProfile['locked']>;

/** Per-field rendering rules; everything defaults to an optional, valid, editable text box. */
export interface FieldOptions {
  required?: boolean;
  optional?: boolean;
  placeholder?: string;
  inputMode?: 'tel' | 'email';
  invalid?: string | null;
}

/** Phone validator: 10-15 digits; allows digits, spaces, and `()+-`. Empty is valid. */
export function isValidPhone(raw: string): boolean {
  const value = raw.trim();
  if (!value) return true;
  if (/[^0-9 ()+-]/.test(value)) return false;
  const digits = value.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 15;
}

/** Loose on purpose: enough to catch a typo, not enough to argue with a valid address. */
export function isValidEmail(raw: string): boolean {
  const value = raw.trim();
  return !value || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);
}

/**
 * Reads one field off the resolved profile: the server's value where a system of record claimed it,
 * the user's own keystrokes where none did.
 *
 * A claim with nothing behind it is treated as no claim. That should not happen, but the cost of
 * believing it would be a required field nobody can fill in, which is the bug the per-field locking
 * replaced in the first place.
 */
export function resolve(
  profile: UserProfile | undefined,
  key: FieldKey,
  entered: string,
): ResolvedField {
  const source = profile?.locked?.[key];
  const claimed = profile?.[key]?.trim() ?? '';
  return source && claimed
    ? { value: claimed, locked: true, source }
    : { value: entered, locked: false, source: null };
}

/**
 * The same field as a dialog that only borrows it sees: anything already known is shown rather than
 * asked for again, because it is corrected where it is owned and not in passing.
 *
 * A value no system of record claimed is still the user's, so it is attributed to their profile;
 * a blank one is left editable, since borrowing is no reason to leave a gap unfilled.
 */
export function borrowed(field: ResolvedField): ResolvedField {
  return field.locked || !field.value.trim()
    ? field
    : { value: field.value, locked: true, source: 'profile' };
}

/** Spells a stored code out for display, leaving anything off-list as-is rather than blanking it. */
export function labelFor(options: GeoOption[], code: string): string {
  return options.find((o) => o.code === code.trim().toUpperCase())?.name ?? code;
}

/**
 * Stable DOM id from a field's label, so the label and its description can point at the input.
 *
 * Keeps letters and digits in any script rather than ASCII alone: the labels are translated, and
 * stripping non-Latin ones would collapse every field in Chinese, Korean, Thai and Russian to the
 * same id, breaking both the label association and the lock hint each input points at.
 */
export function fieldId(label: string): string {
  return `pf-${label.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '-')}`;
}
